/**
 * Utility functions for downloading query result data as CSV or JSON.
 */

/**
 * Generate a download filename with timestamp, optionally prefixed with queryId.
 * Format: {queryId}_{YYYYMMDD_HHmmss}  OR  query_results_{YYYYMMDD_HHmmss}
 */
export function generateFilename(queryId?: string): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const prefix = queryId ? queryId : 'query_results';
  return `${prefix}_${timestamp}`;
}

/**
 * Trigger a browser file download with the given content.
 */
function triggerDownload(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Download an array of row objects as a CSV file.
 */
export function downloadAsCSV(data: any[], queryId?: string) {
  if (!data || data.length === 0) return;

  const columns = Object.keys(data[0]);

  // Escape CSV fields: wrap in quotes if the value contains comma, quote, or newline
  const escapeField = (val: any): string => {
    const str = val !== null && val !== undefined ? String(val) : '';
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const header = columns.map(escapeField).join(',');
  const rows = data.map(row => columns.map(col => escapeField(row[col])).join(','));
  const csv = [header, ...rows].join('\n');

  const filename = generateFilename(queryId) + '.csv';
  triggerDownload(csv, filename, 'text/csv;charset=utf-8;');
}

/**
 * Download an array of row objects as a JSON file.
 */
export function downloadAsJSON(data: any[], queryId?: string) {
  if (!data || data.length === 0) return;

  const json = JSON.stringify(data, null, 2);
  const filename = generateFilename(queryId) + '.json';
  triggerDownload(json, filename, 'application/json;charset=utf-8;');
}

/**
 * Download a SQL query string as a .sql file.
 */
export function downloadAsSql(sql: string, queryId?: string) {
  if (!sql || !sql.trim()) return;

  const filename = generateFilename(queryId) + '.sql';
  triggerDownload(sql, filename, 'text/plain;charset=utf-8;');
}
